import React, { useCallback, useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import {
  DoctorIcon,
  MedicalNotesIcon,
  CalendarIcon,
  NurseIcon,
  RunningIcon,
  HelpingIcon,
  CommentsIcon,
  SupportIcon,
  BriefcaseIcon,
  NotesIcon,
  ChevronDownIcon,
  CloudUploadIcon,
  PrintIcon,
  FileCheckIcon,
} from "../../../Icons";
import ClinicalFormsSection from "./clinicalForms/ClinicalFormsSection";
import PatientEvalDailyLogsSection from "./clinicalForms/PatientEvalDailyLogsSection";
import ClinicalFormUploadModal from "./clinicalForms/ClinicalFormUploadModal";
import ClinicalFormBulkPrintPrompt from "./clinicalForms/ClinicalFormBulkPrintPrompt";
import { applyClinicalFormMarks } from "../../../services/patientService";
import { applyDailyLogMarks } from "../../../services/dailyLogService";
import { printGeneralForms } from "../../Application/GeneralForms/print/generalFormsPrintService";
import {
  CLINICAL_FORM_MARKS,
  selectionMarkMode,
} from "./clinicalForms/clinicalFormMarks";
import {
  clinicalFormsBulkPrintUrl,
  CLINICAL_FORM_BULK_PRINT_MAX,
} from "./clinicalForms/clinicalFormPrint";
import { EVAL_DAILY_LOG_SECTION_KEY } from "./clinicalForms/dailyLogEval";
import { PATIENT_SECTION_TONE, PATIENT_THEME } from "../patientTheme";

/**
 * Eval & Orders sections (top → bottom). Admission Documents lives here so
 * clinicians fill consent / preparedness / NOMNC alongside clinical notes.
 * Selection + Flags use the same Action toolbar as every other section.
 */
const SECTIONS = [
  {
    key: "admission-documents",
    title: "Admission Documents",
    addLabel: "Add Documents",
    icon: FileCheckIcon,
    tone: PATIENT_SECTION_TONE,
  },
  {
    key: "physician-orders",
    title: "Physician Orders",
    addLabel: "Add Physician Orders",
    icon: DoctorIcon,
    tone: PATIENT_SECTION_TONE,
  },
  {
    key: "plan-of-care",
    title: "Plan of Care",
    addLabel: "Add Plan of Care",
    icon: MedicalNotesIcon,
    tone: PATIENT_SECTION_TONE,
  },
  {
    key: "soc-roc-recert",
    title: "SOC/ROC/RECERT/TRANSFER",
    addLabel: "Add SOC/ROC/Recert/Transfer",
    icon: CalendarIcon,
    tone: PATIENT_SECTION_TONE,
  },
  {
    key: "nurse-notes",
    title: "Nurse Eval & Visit Notes",
    addLabel: "Add Nurse Notes",
    icon: NurseIcon,
    tone: PATIENT_SECTION_TONE,
  },
  {
    key: "pt-notes",
    title: "PT Eval & Visit Notes",
    addLabel: "Add PT Notes",
    icon: RunningIcon,
    tone: PATIENT_SECTION_TONE,
  },
  {
    key: "ot-notes",
    title: "OT Eval & Visit Notes",
    addLabel: "Add OT Notes",
    icon: HelpingIcon,
    tone: PATIENT_SECTION_TONE,
  },
  {
    key: "st-notes",
    title: "ST Eval & Visit Notes",
    addLabel: "Add ST Notes",
    icon: CommentsIcon,
    tone: PATIENT_SECTION_TONE,
  },
  {
    key: "msw-notes",
    title: "MSW Eval & Visit Notes",
    addLabel: "Add MSW Notes",
    icon: SupportIcon,
    tone: PATIENT_SECTION_TONE,
  },
  {
    key: "chha-notes",
    title: "CHHA Visit Notes",
    addLabel: "Add CHHA Notes",
    icon: BriefcaseIcon,
    tone: PATIENT_SECTION_TONE,
  },
  {
    key: EVAL_DAILY_LOG_SECTION_KEY,
    title: "Daily Logs",
    addLabel: "Add Daily Log",
    icon: NotesIcon,
    tone: PATIENT_SECTION_TONE,
    kind: "daily-log",
  },
];

/**
 * Eval & Orders — global Action + Upload Document for all sections.
 * Selection is shared across every section list.
 */
const PatientEvalOrdersTab = ({
  patientId,
  canEdit,
  canDelete = false,
  /** Multi-select / bulk Action — Admin & OA only (not RN/LVN/PT…). */
  showSelection = false,
  patient,
  chartId,
  episodeId,
  focusSectionKey = null,
  focusNonce = 0,
}) => {
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const sectionFormIdsRef = useRef({});
  const formsByIdRef = useRef(new Map());
  const [formsVersion, setFormsVersion] = useState(0);
  const [reloadKey, setReloadKey] = useState(0);
  const [actionOpen, setActionOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [printPromptOpen, setPrintPromptOpen] = useState(false);
  const [applying, setApplying] = useState(false);
  const actionRef = useRef(null);

  useEffect(() => {
    setSelectedIds(new Set());
    sectionFormIdsRef.current = {};
    formsByIdRef.current = new Map();
    setFormsVersion((v) => v + 1);
  }, [patientId, chartId, episodeId]);

  useEffect(() => {
    if (!focusNonce) return;
    setReloadKey((k) => k + 1);
  }, [focusNonce]);

  useEffect(() => {
    if (!focusSectionKey) return undefined;
    let cancelled = false;
    let attempts = 0;
    const tryScroll = () => {
      if (cancelled) return;
      const el = document.getElementById(`eval-section-${focusSectionKey}`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        return;
      }
      if (attempts < 25) {
        attempts += 1;
        window.setTimeout(tryScroll, 120);
      }
    };
    const start = window.setTimeout(tryScroll, 80);
    return () => {
      cancelled = true;
      window.clearTimeout(start);
    };
  }, [focusSectionKey, focusNonce, patientId, reloadKey]);

  useEffect(() => {
    if (!actionOpen) return undefined;
    const onDoc = (e) => {
      if (actionRef.current && !actionRef.current.contains(e.target)) {
        setActionOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [actionOpen]);

  const hasSelection = selectedIds.size > 0;

  const notifyFormsLoaded = useCallback((sectionKey, forms) => {
    const newIds = (forms || []).map((f) => f.id);
    const oldIds = sectionFormIdsRef.current[sectionKey] || [];
    sectionFormIdsRef.current = {
      ...sectionFormIdsRef.current,
      [sectionKey]: newIds,
    };
    const map = formsByIdRef.current;
    oldIds.forEach((id) => {
      if (!newIds.includes(id)) map.delete(id);
    });
    (forms || []).forEach((f) => {
      if (f?.id != null) map.set(f.id, f);
    });
    setFormsVersion((v) => v + 1);
    const newIdSet = new Set(newIds);
    setSelectedIds((prev) => {
      const next = new Set(prev);
      oldIds.forEach((id) => {
        if (!newIdSet.has(id)) next.delete(id);
      });
      return next;
    });
  }, []);

  const selectedFormsForMarks = (() => {
    // formsVersion keeps Action menu in sync when section data reloads
    void formsVersion;
    return Array.from(selectedIds)
      .map((id) => formsByIdRef.current.get(id))
      .filter(Boolean);
  })();

  const toggleSelect = useCallback((id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleSelectAllInSection = useCallback((formIds, selectAll) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      (formIds || []).forEach((id) => {
        if (selectAll) next.add(id);
        else next.delete(id);
      });
      return next;
    });
  }, []);

  const refreshAll = useCallback(() => {
    setReloadKey((k) => k + 1);
  }, []);

  const partitionSelection = () => {
    void formsVersion;
    const selected = Array.from(selectedIds)
      .map((id) => formsByIdRef.current.get(id))
      .filter(Boolean);
    const dailyLogs = selected.filter((form) => form.source === "daily-log");
    const clinical = selected.filter((form) => form.source !== "daily-log");
    return { selected, dailyLogs, clinical };
  };

  const applyMark = async (markType, action = "apply") => {
    const { dailyLogs, clinical } = partitionSelection();
    if (!dailyLogs.length && !clinical.length) {
      toast.error("Select at least one form");
      return;
    }
    if (dailyLogs.length && clinical.length) {
      toast.error("Select only Daily Logs or only clinical forms for flags");
      return;
    }
    const isRemove = action === "remove";
    setApplying(true);
    setActionOpen(false);
    try {
      let updated = 0;
      if (dailyLogs.length) {
        const res = await applyDailyLogMarks({
          formIds: dailyLogs.map((form) => form.dailyLogId),
          markType,
          action: isRemove ? "remove" : "apply",
        });
        updated = res?.result?.updatedCount ?? res?.updatedCount ?? dailyLogs.length;
      } else {
        const formIds = clinical.map((form) => form.id);
        const res = await applyClinicalFormMarks(patientId, {
          formIds,
          markType,
          action: isRemove ? "remove" : "apply",
        });
        updated = res?.result?.updatedCount ?? res?.updatedCount ?? formIds.length;
      }
      const meta = CLINICAL_FORM_MARKS.find((m) => m.type === markType);
      toast.success(
        isRemove
          ? `${meta?.label || markType} removed from ${updated} form${
              updated === 1 ? "" : "s"
            }`
          : `${meta?.label || markType} applied to ${updated} form${
              updated === 1 ? "" : "s"
            }`,
      );
      setSelectedIds(new Set());
      refreshAll();
    } catch (err) {
      toast.error(err.message || "Failed to update flags");
    } finally {
      setApplying(false);
    }
  };

  const handleRemoveMark = async (form, markType) => {
    if (!form?.id || !markType) return;
    try {
      if (form.source === "daily-log") {
        await applyDailyLogMarks({
          formIds: [form.dailyLogId],
          markType,
          action: "remove",
        });
      } else {
        await applyClinicalFormMarks(patientId, {
          formIds: [form.id],
          markType,
          action: "remove",
        });
      }
      const meta = CLINICAL_FORM_MARKS.find((m) => m.type === markType);
      toast.success(`${meta?.label || markType} removed`);
      refreshAll();
    } catch (err) {
      toast.error(err.message || "Failed to remove flag");
    }
  };

  const openBulkPrintPrompt = () => {
    const { dailyLogs, clinical } = partitionSelection();
    if (!dailyLogs.length && !clinical.length) {
      toast.error("Select at least one form");
      return;
    }
    if (dailyLogs.length && clinical.length) {
      toast.error("Select only Daily Logs or only clinical forms to print");
      return;
    }
    const count = dailyLogs.length || clinical.length;
    if (count > CLINICAL_FORM_BULK_PRINT_MAX) {
      toast.error(
        `Select at most ${CLINICAL_FORM_BULK_PRINT_MAX} forms to print at once`,
      );
      return;
    }
    setActionOpen(false);
    if (dailyLogs.length) {
      printGeneralForms(
        dailyLogs.map((form) => ({ id: form.dailyLogId })),
        "daily-log",
      );
      return;
    }
    setPrintPromptOpen(true);
  };

  const handlePrintChoice = async (choice) => {
    setPrintPromptOpen(false);
    if (!choice) return;
    const { dailyLogs, clinical } = partitionSelection();
    if (dailyLogs.length) {
      await printGeneralForms(
        dailyLogs.map((form) => ({ id: form.dailyLogId })),
        "daily-log",
      );
      return;
    }
    const formIds = clinical.map((form) => form.id);
    if (!formIds.length) {
      toast.error("Select at least one form");
      return;
    }
    const url = clinicalFormsBulkPrintUrl(patientId, formIds, {
      includeAttachments: choice === "with",
    });
    const toastId = toast.loading(
      formIds.length === 1
        ? "Opening print preview…"
        : `Opening print preview for ${formIds.length} forms…`,
    );
    const win = window.open(url, "_blank", "noopener,noreferrer");
    if (!win) {
      toast.error("Pop-up blocked — allow pop-ups to open the print preview", {
        id: toastId,
      });
      return;
    }
    toast.success("Print preview opened in a new tab", { id: toastId });
  };

  return (
    <div className="flex flex-col gap-3">
      {canEdit ? (
        <div className="flex flex-wrap items-center justify-between gap-2 bg-white border border-slate-200 rounded-lg px-3 py-2 shadow-sm">
          <p className="text-[11px] text-slate-500">
            {showSelection
              ? hasSelection
                ? `${selectedIds.size} form${selectedIds.size === 1 ? "" : "s"} selected across sections`
                : "Select forms in any section, then use Action or Upload Document"
              : "Open a form to fill or review. Use Upload Document to attach files."}
          </p>
          <div className="flex flex-wrap items-center gap-1.5">
            {showSelection ? (
              <div className="relative" ref={actionRef}>
                <button
                  type="button"
                  disabled={!hasSelection || applying}
                  onClick={() => setActionOpen((v) => !v)}
                  className={`inline-flex items-center gap-1 h-8 px-3 text-xs font-medium border rounded-md transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                    hasSelection
                      ? PATIENT_THEME.button.outline
                      : "text-slate-500 bg-white border-slate-200"
                  }`}
                  title={
                    hasSelection
                      ? "Apply action to selected forms"
                      : "Select forms to enable Action"
                  }
                >
                  Action
                  <ChevronDownIcon size={12} />
                </button>
                {actionOpen ? (
                  <div className="absolute right-0 top-full mt-1 z-40 w-56 rounded-lg border border-slate-200 bg-white shadow-lg py-1">
                    <p className="px-3 py-1 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                      Select an Action
                    </p>
                    <button
                      type="button"
                      onClick={openBulkPrintPrompt}
                      className={`w-full flex items-center gap-2 px-3 py-1.5 text-left text-xs text-slate-700 ${PATIENT_THEME.menuHover}`}
                    >
                      <span className="inline-flex h-5 w-5 items-center justify-center rounded-md bg-slate-700 text-white shrink-0">
                        <PrintIcon size={12} strokeWidth={2.25} />
                      </span>
                      Print
                    </button>
                    {(() => {
                      const markModes = CLINICAL_FORM_MARKS.map((mark) => ({
                        mark,
                        mode: selectionMarkMode(
                          selectedFormsForMarks,
                          selectedIds,
                          mark.type,
                        ),
                      }));
                      const applyMarks = markModes.filter(
                        ({ mode }) => mode === "apply" || mode === "both",
                      );
                      const removeMarks = markModes.filter(
                        ({ mode }) => mode === "remove" || mode === "both",
                      );
                      const renderMarkButton = (mark, action) => {
                        const Icon = mark.Icon;
                        return (
                          <button
                            key={`${mark.type}-${action}`}
                            type="button"
                            onClick={() => applyMark(mark.type, action)}
                            className={`w-full flex items-center gap-2 px-3 py-1.5 text-left text-xs text-slate-700 ${PATIENT_THEME.menuHover}`}
                          >
                            <span
                              className="inline-flex h-5 w-5 items-center justify-center rounded-md text-white shrink-0"
                              style={{ backgroundColor: mark.color }}
                            >
                              {Icon ? (
                                <Icon size={12} strokeWidth={2.25} />
                              ) : (
                                mark.code
                              )}
                            </span>
                            {mark.label}
                          </button>
                        );
                      };
                      return (
                        <>
                          {applyMarks.length > 0 ? (
                            <>
                              <div className="my-1 border-t border-slate-100" />
                              <p className="px-3 py-1 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                                Mark
                              </p>
                              {applyMarks.map(({ mark }) =>
                                renderMarkButton(mark, "apply"),
                              )}
                            </>
                          ) : null}
                          {removeMarks.length > 0 ? (
                            <>
                              <div className="my-1 border-t border-slate-100" />
                              <p className="px-3 py-1 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                                Remove
                              </p>
                              {removeMarks.map(({ mark }) =>
                                renderMarkButton(mark, "remove"),
                              )}
                            </>
                          ) : null}
                        </>
                      );
                    })()}
                  </div>
                ) : null}
              </div>
            ) : null}

            <button
              type="button"
              onClick={() => setUploadOpen(true)}
              className={`inline-flex items-center gap-1 h-8 px-3 text-xs font-medium border rounded-md transition-colors ${PATIENT_THEME.button.outline}`}
            >
              <CloudUploadIcon size={13} />
              Upload Document
            </button>
          </div>
        </div>
      ) : null}

      {SECTIONS.map((section) => (
        <div
          key={`${section.key}-${reloadKey}`}
          id={`eval-section-${section.key}`}
        >
          {section.kind === "daily-log" ? (
            <PatientEvalDailyLogsSection
              patientId={patientId}
              title={section.title}
              addLabel={section.addLabel}
              icon={<section.icon size={14} />}
              tone={section.tone}
              canEdit={canEdit}
              canDelete={canDelete}
              canManageFlags={showSelection}
              patient={patient}
              selectedIds={selectedIds}
              onToggleSelect={toggleSelect}
              onToggleSelectAll={toggleSelectAllInSection}
              onFormsLoaded={notifyFormsLoaded}
              onRemoveMark={handleRemoveMark}
              showSelection={showSelection}
              showMarks
              showCountBadge={false}
              forceOpen={focusSectionKey === section.key}
              highlight={focusSectionKey === section.key}
            />
          ) : (
            <ClinicalFormsSection
              patientId={patientId}
              chartId={chartId}
              episodeId={episodeId}
              sectionKey={section.key}
              title={section.title}
              addLabel={section.addLabel}
              icon={<section.icon size={14} />}
              tone={section.tone}
              canEdit={canEdit}
              canDelete={canDelete}
              canManageFlags={showSelection}
              patient={patient}
              selectedIds={selectedIds}
              onToggleSelect={toggleSelect}
              onToggleSelectAll={toggleSelectAllInSection}
              onFormsLoaded={notifyFormsLoaded}
              onRemoveMark={handleRemoveMark}
              showSelection={section.showSelection ?? showSelection}
              showMarks={section.showMarks ?? true}
              showCountBadge={section.showCountBadge ?? false}
              forceOpen={focusSectionKey === section.key}
              highlight={focusSectionKey === section.key}
            />
          )}
        </div>
      ))}

      <ClinicalFormUploadModal
        open={uploadOpen}
        patientId={patientId}
        patient={patient}
        defaultSectionKey={
          selectedIds.size === 1 &&
          formsByIdRef.current.get(Array.from(selectedIds)[0])?.source ===
            "daily-log"
            ? EVAL_DAILY_LOG_SECTION_KEY
            : "physician-orders"
        }
        defaultFormId={
          selectedIds.size === 1
            ? (() => {
                const form = formsByIdRef.current.get(
                  Array.from(selectedIds)[0],
                );
                if (!form) return null;
                return form.source === "daily-log" ? form.dailyLogId : form.id;
              })()
            : null
        }
        onClose={() => setUploadOpen(false)}
        onUploaded={() => {
          setUploadOpen(false);
          refreshAll();
        }}
      />

      <ClinicalFormBulkPrintPrompt
        open={printPromptOpen}
        formCount={selectedIds.size}
        onChoose={handlePrintChoice}
      />
    </div>
  );
};

export default PatientEvalOrdersTab;
